/**
 * UserPresentListContainer
 * --
 */
import React, { useState, useMemo } from 'react';
import MessageAlert from 'utils';
import PresentListPresenter from './PresentListPresenter';
import dayjs from 'dayjs';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';
import { useGetUsers } from 'hooks/UserHooks';
import { DOMAIN_URL, DEPLOY_TYPE, stringToMoneyFormat } from 'utils';
import { useCancelPresent, useGetPresents } from 'hooks/PresentHooks';
import { useGetOrders } from 'hooks/OrderHooks';
import { useGetShopOrders } from 'hooks/ShopOrderHooks';
import { useBizSendLMS } from 'hooks/BizHooks';
/**----------------------------------------
 * [Component] 총관리자: 회원관리 -> 선물내역
 * ----------------------------------------
 */
const INITIAL_VALUE = {
  start_date: dayjs().subtract(1, 'months').startOf('day'),
  end_date: dayjs().endOf('day'),
  value: 30,
  // type: 'ALL',
  // isReward: 'REWARD',
  // reward_stamp: 0,
  // expired_date: 0,
  // update_date: 0,
};
const SEARCH_KEYWORD = {
  product_name: null,
  barcode: null,
  send_user: null,
  target_user: null,
  shop_information_name: null,
};
const PresentListContainer = ({ permission }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);

  const [currentPresent, setCurrentPresent] = useState(null);
  const [period, setPeriod] = useState({
    start_date: dayjs().subtract(1, 'months').startOf('day').unix(),
    end_date: dayjs().endOf('day').unix(),
  });
  const [inputValue, setInputValue] = React.useState();
  const [selectedList, setSelectedList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dateValue, setDateValue] = useState(INITIAL_VALUE);
  const [searchValue, setSearchValue] = useState(SEARCH_KEYWORD);
  // const [currentTab, setCurrentTab] = useState('all-list');
  const [modalData, setModalData] = React.useState(null);

  /* ===== MUTATE ===== */
  const { cancelPresent } = useCancelPresent();
  const { sendLMS } = useBizSendLMS();
  const { data: ShopInformationList } = useGetShopInformations(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });
  const { data: UserList } = useGetUsers(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });
  const { data: PresentList, refetch: refetchPresentList } = useGetPresents(
    { start_date: period.start_date, end_date: period.end_date },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
    }
  );
  const { data: OrderList } = useGetOrders(
    { filter: 'present_info' },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
    }
  );
  const { data: ShopOrderList } = useGetShopOrders(
    { payment_method: 'PRESENT', join_tables: '["parcel_service"]' },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
    }
  );
  const newPresentList = useMemo(() => {
    setLoading(true);
    try {
      //회원리스트
      const userList = UserList?.filter((user) =>
        PresentList?.some(
          (present) =>
            present.user_id === user.user_id ||
            present.target_id === user.user_id
        )
      );
      //선물리스트
      const presentList = PresentList?.map((present) => {
        //선물한 회원
        const matchedSendUser = userList?.find(
          (user) => present.user_id === user.user_id
        );
        //선물 받은 회원
        const matchedTargetUser = userList?.find(
          (user) => present.target_id === user.user_id
        );

        //주문 정체
        const matchedOrder = OrderList?.filter(
          (order) =>
            order?.user_id === present?.target_id &&
            order?.present_info?.some(
              (menu) => menu?.present_id === present?.present_id
            )
        )?.map((order) => {
          const matchedShopInformation = ShopInformationList?.find(
            (shop) => shop?.shop_information_id === order?.shop_information_id
          );

          return {
            ...order,
            shop_information: matchedShopInformation,
          };
        });

        //상품 주문 정체
        const matchedShopOrder = ShopOrderList?.filter(
          (shop_order) =>
            present?.target_id === shop_order?.user_id &&
            shop_order?.product_information?.some(
              (product) => product?.present_id === present?.present_id
            )
        )?.map((shop_order) => {
          const matchedShopInformation = ShopInformationList?.find(
            (shop) =>
              shop?.shop_information_id === shop_order?.shop_information_id
          );
          return {
            ...shop_order,
            shop_information: matchedShopInformation,
            parcel_service: shop_order?.parcel_service?.[0],
          };
        });

        return {
          key: present.present_id,
          shop_information_name: present?.product?.shop_information_name,
          send_user: matchedSendUser,
          target_user: {
            ...matchedTargetUser,
            user_name: matchedTargetUser?.user_name || '비회원',
          },
          order: matchedOrder,
          shop_order: matchedShopOrder,
          ...present,
        };
      });

      return presentList?.sort((a, b) => b.present_id - a.present_id);
    } catch (e) {
      MessageAlert.error('데이터 조회 실패');
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [UserList, PresentList, OrderList, ShopOrderList, ShopInformationList]);
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */

  /**
   *
   * @param {string} t
   * @param {string | number} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /**
   *
   * @param {string} t
   * @param {string | number} v
   */
  const handleChangeSearchValue = (t, v) => {
    setSearchValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /**
   * 데이터 리패치
   */
  const refreshData = () => {
    refetchPresentList();
  };

  /**--------------------
   * 선물 결제 취소
   * --------------------
   */
  const handlePresentCancel = async () => {
    setLoading(true);
    try {
      const result = await cancelPresent({ present_id: selectedId });
      if (result.status !== 200) {
        MessageAlert.error('결제 취소를 실패하였습니다.');
        return;
      }
      setSelectedId(null);
      refreshData();
      MessageAlert.success('결제 취소가 성공하였습니다.');
    } catch (err) {
      console.error('[handlePresentCancel] Error!! ', err);
      MessageAlert.error('결제 취소를 실패하였습니다.');
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };
  /**--------------------
   * 선물 문자 재전송
   * --------------------
   */
  const handleReSendPresentMessage = async (v) => {
    try {
      const {
        target_user,
        product,
        barcode,
        expired_date,
        created_at,
        message,
      } = v;

      const protocol = window.location.protocol;
      const port = DEPLOY_TYPE === 'local' ? ':3000' : '';
      // const domain =
      //   DEPLOY_TYPE === 'development' ? `jm-coffee.${DOMAIN_URL}` : DOMAIN_URL;
      const domain =
        DEPLOY_TYPE === 'development'
          ? `mall-jm-coffee.${DOMAIN_URL}`
          : `mall.${DOMAIN_URL}`;

      const presentMessage = {
        to_phone: target_user?.user_phone,
        type: 'lms',
        is_template: 'Y',
        replace_value: {
          PRODUCT: product?.product_name || product?.menu_name,
          BARCODE: barcode,
          DATE: dayjs.unix(expired_date).format('YYYYMMDD-HH:mm:ss'),
          PRICE: `${stringToMoneyFormat(product?.price)} 원`,
          PRODUCT_COUNT: `${product?.num} 개`,
          LINK: `${protocol}//${domain}${port}/#/common/present/${barcode}?serial=${created_at}`,
          MESSAGE: message,
        },

        send_type: 'PRESENT',
        user_type: 'USER',
        user_id: v?.user_id,
        shop_information_id: 1,
      };
      const result = await sendLMS(presentMessage);
      if (result.status !== 200) {
        MessageAlert.error('문자 재전송에 실패했습니다.');
        return;
      }
      MessageAlert.success('문자 재전송에 성공하였습니다.');
      console.log('presentMessage', presentMessage);
    } catch (err) {
      console.error('[handlePresentCancel] Error!! ', err);
      MessageAlert.error('결제 취소를 실패하였습니다.');
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  /**
   * 회원 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array | number} obj
   */
  const handleSelect = (type, obj) => {
    const selectType = type.toLocaleUpperCase();

    switch (selectType) {
      case 'CANCEL':
        setSelectedId(obj);
        return;
      case 'CHECKED': // 개별 선택
        setSelectedList((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setSelectedList((prev) =>
          prev.filter((select) => select.key !== obj.key)
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setSelectedList(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = selectedList.filter((user) =>
          obj.some((ob) => ob.key === user.key)
        );

        setSelectedList(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = selectedList.length === obj.length;

        if (!isAll) {
          setSelectedList(obj);
        } else {
          setSelectedList([]);
        }
        return;

      case 'RESET':
        setSelectedList([]);
        return;
      default:
        return;
    }
  };

  /* ===== HOOKS ===== */
  // /* 초기 설정 & 업체변경 시 반영 */
  // useEffect(() => {
  //   currentShop && handleGetReviewList();
  // }, [currentShop]);

  /* ===== RENDER ===== */
  return (
    <PresentListPresenter
      {...{
        /* States */
        presentList: newPresentList,
        permission,
        loading,
        currentPresent,
        setCurrentPresent,
        period,
        setPeriod,
        onChange: handleChange,
        inputValue: inputValue,
        setInputValue: setInputValue,
        modalData,
        setModalData,
        /* Events */
        setLoading,
        onSelect: handleSelect,
        onSelectId: setSelectedId,
        onPresentCancel: handlePresentCancel,
        onReSendPresentMessage: handleReSendPresentMessage,
        selectedList,
        INITIAL_VALUE,
        searchValue,
        dateValue,
        setDateValue,
        onChangeSearchValue: handleChangeSearchValue,
      }}
    />
  );
};

/* DF */
PresentListContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};
export default PresentListContainer;
